import produce, {castDraft, Draft} from 'immer';

import {Metric} from 'app_api/projects/metrics/types';
import {BehaviorEntity as Behavior} from 'app_api/projects/behaviors/types';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {
  isCohortMenuSectionItem,
  isEventMenuSectionItem,
  isFormulaMenuSectionItem,
  isFunnelMenuSectionItem,
  isProfileMenuSectionItem,
  isRetentionMenuSectionItem,
  isSavedMetricMenuSectionItem,
  isSimpleMenuSectionItem,
  MultiMetricsSectionItem,
} from 'common/query-builder/query-block/multi-metrics-menu/types';
import {isBehaviorShowClause, isFormulaShowClause} from 'common/report/insights/models/utils';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {MetricsResource} from 'common/report/model-resources/metrics';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {updateOrAddAtIndex} from 'common/util/array';
import {isSavedCohort, isScdProperty} from 'common/query-builder/query-block/properties-menu/types';
import {PropertyType} from 'common/report/constants';
import {insightsFilterAttrsFromPropertyFilter} from 'common/widgets/property-filter-menu/models/insights-filter';
import {convertPropertySelectiontoBookmarkProperty} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/metric-entry-util';

import {FILTER_DETERMINER_ANY} from '../util/constants';
import {ShowClause} from '../util/types';
import {DEFAULT_CLAUSE_BY_TYPE} from './constants';

/** these types allow passing a MetricType and dynamically type it to the corresponding show clause tye */
export type MetricType = BookmarkTypes.TopLevelMetricType.Metric | BookmarkTypes.TopLevelMetricType.Formula;
export type MetricTypeMap<T> = T extends BookmarkTypes.TopLevelMetricType.Metric
  ? BookmarkTypes.BehaviorShowClause
  : T extends BookmarkTypes.TopLevelMetricType.Formula
    ? BookmarkTypes.FormulaShowClause
    : never;

export type ValidFormulaUpdateFields = Pick<
  BookmarkTypes.FormulaShowClause,
  `definition` | `measurement` | `display` | `isExpanded` | `isHidden` | `name` | `referencedMetrics`
>;

/**
 * Returns a top level metric or a metric referenced inside a formula
 */
export function getMetricAtIndex<T extends MetricType>({
  index,
  parentMetricIndex,
  params,
  type,
}: {
  index: number;
  parentMetricIndex?: number;
  params: Draft<BookmarkTypes.InsightsBookmarkParams> | BookmarkTypes.InsightsBookmarkParams;
  type?: T;
}) {
  const clause = params.sections.show[parentMetricIndex ?? index];

  if (!clause) {
    return null;
  }

  if (type === BookmarkTypes.TopLevelMetricType.Metric && !isBehaviorShowClause(clause)) {
    throw new Error(`Metric action not supported.`);
  } else if (type === BookmarkTypes.TopLevelMetricType.Formula && !isFormulaShowClause(clause)) {
    throw new Error(`Formula action not supported.`);
  }

  if (parentMetricIndex !== undefined) {
    // validates against nested formulas
    if (!isFormulaShowClause(clause)) {
      throw Error(`Unsupported action for formula`);
    }
    return (clause.referencedMetrics ?? [])[index] ?? null;
  }

  return clause as MetricTypeMap<T>;
}

/**
 * Applies a new clause selection to a top level metric or a referenced metric inside a formula
 */
export function updateMetricOrReferencedMetric({
  index,
  parentMetricIndex,
  params,
  newClause,
}: {
  index: number;
  parentMetricIndex?: number;
  params: Draft<BookmarkTypes.InsightsBookmarkParams>;
  newClause: ShowClause;
}) {
  const clause = params.sections.show[parentMetricIndex ?? index];

  if (parentMetricIndex !== undefined) {
    // validates against nested formulas
    if (!isFormulaShowClause(clause) || newClause.type !== BookmarkTypes.TopLevelMetricType.Metric) {
      throw Error(`Unsupported action for formula`);
    }

    // update the parent formula metric
    params.sections.show[parentMetricIndex] = {
      ...clause,
      referencedMetrics: updateOrAddAtIndex(clause.referencedMetrics ?? [], index, newClause),
      hasUnsavedChanges: true,
    };
  } else {
    params.sections.show = updateOrAddAtIndex(params.sections.show ?? [], index, newClause);
  }
}

/**
 * Creates a new show clause based on a metric model selection
 */
export function getClauseFromMetricModel(metric: Metric): ShowClause {
  const commonAttributes = {
    id: metric.id,
    name: metric.name,
    hasUnsavedChanges: false,
    isExpanded: true,
    display: metric.definition.display ?? null,
    measurement: metric.definition.measurement ?? null,
  };

  // handles saved formula selection
  if (metric.type === BookmarkTypes.TopLevelMetricType.Formula) {
    return {
      type: BookmarkTypes.TopLevelMetricType.Formula,
      ...commonAttributes,
      ...metric.definition.formula,
    } satisfies BookmarkTypes.FormulaShowClause;
  } else {
    // handles a behavior based metric selection
    return {
      type: BookmarkTypes.TopLevelMetricType.Metric,
      ...commonAttributes,
      ...metric.definition,
    } satisfies BookmarkTypes.BehaviorShowClause;
  }
}

/**
 * Creates a new show clause based on a saved behavior  model selection
 */
export function getClauseFromBehaviorModel({
  behavior,
  clause,
}: {
  behavior: Behavior;
  clause?: BookmarkTypes.BehaviorShowClause | null;
}): BookmarkTypes.BehaviorShowClause {
  if (clause && clause.behavior!.type !== behavior.type) {
    throw new Error(`Attempted to select a different behavior type`);
  }

  const behaviorFromModel = {
    type: behavior.type as BookmarkTypes.MetricType,
    name: behavior.name,
    id: behavior.id,
    ...behavior.definition.behavior,
  };

  // gets the default measurement for each behavior type
  const defaultMeasurement: BookmarkTypes.BehaviorMeasurement = DEFAULT_CLAUSE_BY_TYPE[behavior.type]?.measurement || {
    math: BookmarkTypes.PRIMITIVE_MATH_TYPES.UNIQUE,
  };

  /**
   * @todo(metrics) perform clause cleanup and validation for the measurement field
   * For example: if the selected behavior changes from 3 to 2 steps, the measurement for "Conversion Step 2-3" becomes invalid
   */

  return {
    type: BookmarkTypes.TopLevelMetricType.Metric,
    measurement: clause?.measurement ?? defaultMeasurement,
    ...clause,
    behavior: behaviorFromModel,
  };
}

/**
 * Returns a new ShowClause based on a selected metrics menu item
 */
export function getShowClauseFromMetricItemSelection({
  dataGroupId,
  selectedItem,
}: {
  dataGroupId: string | null;
  selectedItem: MultiMetricsSectionItem;
}): ShowClause {
  let clause: ShowClause;

  /** a raw event selection results in a new metric with a new simple behavior */
  if (isEventMenuSectionItem(selectedItem)) {
    const selectedEvent = selectedItem.value as EventItem;
    clause = {
      type: BookmarkTypes.TopLevelMetricType.Metric,
      measurement: DEFAULT_CLAUSE_BY_TYPE[BookmarkTypes.MetricType.Simple]!.measurement,
      behavior: {
        type: BookmarkTypes.MetricType.Simple,
        name: selectedEvent.label as string,
        resourceType: BookmarkTypes.InsightsResourceType.Events,
        behaviors: [
          {
            type: selectedEvent.custom ? BookmarkTypes.MetricType.CustomEvent : BookmarkTypes.MetricType.Event,
            id: selectedEvent.id ?? undefined,
            name: selectedEvent.value,
            filters: [],
            filtersDeterminer: FILTER_DETERMINER_ANY,
          },
        ],
      },
    } satisfies BookmarkTypes.BehaviorShowClause;
  } else if (
    isSimpleMenuSectionItem(selectedItem) ||
    isFunnelMenuSectionItem(selectedItem) ||
    isRetentionMenuSectionItem(selectedItem)
  ) {
    /** menu selections like saved funnel, retention, or simple behaviors will result in a new metric with a saved behavior */
    const selectedBehaviorModel = selectedItem.value!;
    clause = getClauseFromBehaviorModel({behavior: selectedBehaviorModel});
  } else if (isFormulaMenuSectionItem(selectedItem) || isSavedMetricMenuSectionItem(selectedItem)) {
    /** handles saved formula or metric selection */
    clause = getClauseFromMetricModel(selectedItem.value!);
  } else if (isCohortMenuSectionItem(selectedItem)) {
    /** handles cohort selection */
    clause = produce(DEFAULT_CLAUSE_BY_TYPE[BookmarkTypes.MetricType.Cohort]!, (draft) => {
      if (isSavedCohort(selectedItem.value!)) {
        draft.behavior!.id = selectedItem.value.value;
        draft.behavior!.dataGroupId = selectedItem.value.dataGroupId;
      } else {
        draft.behavior!.name = selectedItem.value!.value.cohort.name;
        draft.behavior!.raw_cohort = selectedItem.value!.value.cohort.raw_cohort;
      }
    });
  } else if (isProfileMenuSectionItem(selectedItem)) {
    /** handles profile and profile property selection */
    const itemValue = selectedItem.value;
    clause = produce(DEFAULT_CLAUSE_BY_TYPE[BookmarkTypes.MetricType.People]!, (draft) => {
      /** handle selecting SCD properties for profile metrics */
      if (itemValue && itemValue !== `$all_people` && isScdProperty(itemValue)) {
        if (itemValue.type === PropertyType.Number) {
          /** create a numeric aggregation when selecting an SCD property from the metrics menu  */
          draft.measurement!.property = convertPropertySelectiontoBookmarkProperty(itemValue);
        } else {
          /** adds a "as of time of event" filter when selecting a string SCD property from the metrics menu */
          draft.behavior!.filters = [
            insightsFilterAttrsFromPropertyFilter({
              ...(itemValue.customProperty ? {customProperty: itemValue.customProperty} : {}),
              ...(itemValue.customPropertyId ? {customPropertyId: itemValue.customPropertyId} : {}),
              ...(itemValue.dataGroupId ? {dataGroupId: itemValue.dataGroupId} : {}),
              ...(itemValue.tempDataGroupId ? {tempDataGroupId: itemValue.tempDataGroupId} : {}),
              resourceType: itemValue.resourceType,
              propertyDefaultType: itemValue.propertyDefaultType || itemValue.type,
              propertyName: itemValue.value,
              ...(itemValue.behavior ? {behavior: itemValue.behavior} : {}),
              propertyType: itemValue.type,
              ...(itemValue.joinPropertyType ? {joinPropertyType: itemValue.joinPropertyType} : {}),
              ...(itemValue.propertyObjectKey ? {propertyObjectKey: itemValue.propertyObjectKey} : {}),
              ...(itemValue.scdEvent ? {scdEvent: itemValue.scdEvent} : {}),
            }),
          ];
        }
      }
    });
  }

  if (
    dataGroupId &&
    isBehaviorShowClause(clause!) &&
    clause.behavior!.type !== BookmarkTypes.MetricType.Cohort &&
    clause.behavior!.type !== BookmarkTypes.MetricType.People
  ) {
    clause.measurement!.dataGroupId = dataGroupId;
  }

  return clause!;
}

/**
 * Synchronizes metrics across a bookmark by updating clauses with the latest data from resources.
 *
 * This function performs two main synchronization operations:
 * 1. Updates behavior clauses with the latest behavior definitions from the behaviors resource
 * 2. Updates metric clauses with the latest metric data from the metrics resource
 *
 * For formula clauses, it also recursively updates any referenced metrics.
 *
 * @param options - The synchronization options
 * @param options.params - The bookmark parameters to update (as a Draft)
 * @param options.behaviors - The behaviors resource containing the latest behavior definitions
 * @param options.metrics - The metrics resource containing the latest metric definitions
 *
 * @returns void - The function updates the params object in-place via Immer's Draft
 */
export function syncMetricsAcrossBookmark({
  params,
  behaviors,
  metrics,
}: {
  params: Draft<BookmarkTypes.InsightsBookmarkParams>;
  behaviors: BehaviorsResource;
  metrics: MetricsResource;
}) {
  const syncBehavior = (clause: Draft<BookmarkTypes.BehaviorShowClause>) => {
    if (!clause.behavior!.id || (clause.behavior!.id && clause.hasUnsavedChanges)) {
      return clause;
    }

    const model = behaviors.getOne(clause.behavior!.id);
    return model.value ? getClauseFromBehaviorModel({behavior: model.value}) : clause;
  };

  const syncClause = (clause: Draft<ShowClause>) => {
    if (!clause.id || (clause.id && clause.hasUnsavedChanges)) {
      return clause;
    }

    const model = metrics.getOne(clause.id);
    clause = model.value ? castDraft(getClauseFromMetricModel(model.value)) : clause;

    // sync any behavior references
    if (clause.type === BookmarkTypes.TopLevelMetricType.Metric) {
      syncBehavior(clause);
    }

    return clause;
  };

  params.sections.show = params.sections.show.map((clause) => {
    clause = syncClause(clause);

    /** updates all referenced metrics in formulas using the metric definition  */
    if (isFormulaShowClause(clause)) {
      clause.referencedMetrics = (clause.referencedMetrics ?? []).map(
        syncClause,
      ) as Array<BookmarkTypes.BehaviorShowClause>;
    }

    return clause;
  });
}
